import { Palette } from './Palette';
import type { MapArchetype } from '../map/MapGen';

/**
 * Per-biome floor themes (ME1). HUE shifts only — every lane stays
 * near-black/low-saturation so backgrounds never compete with bullets
 * (the Palette.ts readability contract). `halls` is the pre-theme cold
 * blue; the vault keeps the classic look so the Reliquary always reads
 * as "elsewhere" regardless of origin biome.
 */
export interface FloorTheme {
  bg: number;
  floor: number;
  floorAlt: number;
  wall: number;
  wallEdge: number;
}

const THEMES: Record<MapArchetype, FloorTheme> = {
  // Rusted umber — oxidized iron over old stone.
  // All non-halls values are LUMA-MATCHED to the halls baseline (green
  // carries ~3.4× red's perceptual weight — verified against screenshots).
  mixed: {
    bg: 0x110c08,
    floor: 0x211712,
    floorAlt: 0x251a14,
    wall: 0x3d2c22,
    wallEdge: 0x563e32,
  },
  // Mossy green-black — wet burrows.
  warrens: {
    bg: 0x070f08,
    floor: 0x0e1a12,
    floorAlt: 0x122016,
    wall: 0x213428,
    wallEdge: 0x2f4936,
  },
  // The original cold blue.
  halls: {
    bg: Palette.bg,
    floor: Palette.floor,
    floorAlt: Palette.floorAlt,
    wall: Palette.wall,
    wallEdge: Palette.wallEdge,
  },
  // Deep purple, dim-gold edges.
  sanctum: {
    bg: 0x0f0a16,
    floor: 0x1c1426,
    floorAlt: 0x201829,
    wall: 0x3a2a4e,
    wallEdge: 0x51422c,
  },
  // The Reliquary keeps the classic look.
  vault: {
    bg: Palette.bg,
    floor: Palette.floor,
    floorAlt: Palette.floorAlt,
    wall: Palette.wall,
    wallEdge: Palette.wallEdge,
  },
};

/** Depth darkening: −3%/floor, clamped at −24% (floor 9). */
function depthScale(floor: number): number {
  return 1 - Math.min(0.24, Math.max(0, floor - 1) * 0.03);
}

function scaleColor(color: number, f: number): number {
  const r = Math.round(((color >> 16) & 0xff) * f);
  const g = Math.round(((color >> 8) & 0xff) * f);
  const b = Math.round((color & 0xff) * f);
  return (r << 16) | (g << 8) | b;
}

export function themeFor(archetype: MapArchetype, floor: number): FloorTheme {
  const base = THEMES[archetype];
  const f = depthScale(floor);
  return {
    bg: scaleColor(base.bg, f),
    floor: scaleColor(base.floor, f),
    floorAlt: scaleColor(base.floorAlt, f),
    wall: scaleColor(base.wall, f),
    wallEdge: scaleColor(base.wallEdge, f),
  };
}
